import { observer } from 'mobx-react-lite';
import React, { useMemo, useCallback, useState } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTheme } from 'styled-components';
import { ThemeEnum } from '@app/common';
import { useRootStore } from '../../../providers/root-store-provider';
import { OnboardingCaseEnum } from '../../../models/enums/onboarding-case-enum';
import { AddTradesOnboardingSubcaseEnum } from '../../../models/enums/add-trades-onboarding-subcase-enum';
import { addTradesManualSteps } from '../../../data/onboarding/add-trades/add-trades-manual-steps';
import { addTradesImportSteps } from '../../../data/onboarding/add-trades/add-trades-import-steps';
import { addTradesStartStep } from '../../../data/onboarding/add-trades/add-trades-start-step';
import { CustomTooltip } from './tooltip/onboarding-tooltip';
import { getOpacity } from '../../../theme/utils/get-rgb';
import {
    shouldSkipTargetValidation,
    handleModalOpeningTransition,
    handleModalClosingTransition,
    handleBackwardNavigation,
    isOnboardingFinished,
    createValidateTargetWithRetry,
    createSafeTransitionTimeout,
    handleJoyrideError,
    handleTargetValidationFailure,
} from './onboarding-helpers';

export const Onboarding: React.FC = observer(() => {
    const { onboardingStore, tradeStore, themeStore } = useRootStore();
    const [showOverlay, setShowOverlay] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const theme = useTheme();

    const validateTarget = useCallback((target: string): boolean => {
        if (target === 'body') return true;
        const element = document.querySelector(target) as HTMLElement;
        return element !== null && element.offsetParent !== null; // Check if element exists and is visible
    }, []);

    const validateTargetWithRetry = useMemo(() => createValidateTargetWithRetry(validateTarget), [validateTarget]);

    const steps: Step[] = useMemo(() => {
        let rawSteps: Step[] = [];

        if (onboardingStore.case === OnboardingCaseEnum.AddTrades) {
            switch (onboardingStore.addTradesSubcase) {
                case AddTradesOnboardingSubcaseEnum.start:
                    rawSteps = [addTradesStartStep];
                    break;

                case AddTradesOnboardingSubcaseEnum.import:
                    rawSteps = [...addTradesImportSteps];
                    break;

                case AddTradesOnboardingSubcaseEnum.manual:
                    rawSteps = [...addTradesManualSteps];
                    break;

                default:
                    rawSteps = [];
            }
        }

        // Return all steps - they will be validated dynamically during navigation
        return rawSteps;
    }, [onboardingStore.case, onboardingStore.addTradesSubcase, validateTarget]);

    // Safety mechanism to prevent getting stuck in transitions
    const safeTransitionTimeout = useMemo(
        () => createSafeTransitionTimeout(isTransitioning, setIsTransitioning, setShowOverlay, onboardingStore),
        [isTransitioning, onboardingStore],
    );

    const pulseColor =
        themeStore.theme === ThemeEnum.Light ? theme.palette.color.purple.main : theme.palette.color.purple.lighten;

    const pulseKeyframes = useMemo(
        () => `
            @keyframes onboarding-pulse {
                0% {
                    box-shadow: 0 0 0 0 ${getOpacity(pulseColor, 0.7)}, 0 0 0 0 ${getOpacity(pulseColor, 0.4)};
                }
                50% {
                    box-shadow: 0 0 0 8px ${getOpacity(pulseColor, 0.3)}, 0 0 0 16px ${getOpacity(pulseColor, 0.1)};
                }
                100% {
                    box-shadow: 0 0 0 0 ${getOpacity(pulseColor, 0)}, 0 0 0 0 ${getOpacity(pulseColor, 0)};
                }
            }
        `,
        [pulseColor],
    );

    const handleJoyrideCallback = useCallback(
        async (data: CallBackProps) => {
            // Prevent multiple concurrent callbacks
            if (isTransitioning) return;

            const { status, step, index } = data;
            const helperParams = { onboardingStore, tradeStore, setShowOverlay, validateTargetWithRetry };

            // Handle errors with recovery attempts
            if (status === STATUS.ERROR) {
                const shouldContinue = await handleJoyrideError(data, onboardingStore, validateTargetWithRetry);
                if (!shouldContinue) return;
            }

            // Check if current step target is still valid with retry logic
            if (step?.target && step.target !== 'body' && !shouldSkipTargetValidation(onboardingStore, index)) {
                const targetValid = await validateTargetWithRetry(step.target as string);
                if (!targetValid) {
                    handleTargetValidationFailure(data, onboardingStore);
                    return;
                }
            }

            // Handle onboarding completion
            if (isOnboardingFinished(data)) {
                await onboardingStore.stopOnboarding();
                return;
            }

            // Set transitioning state for complex operations
            const needsTransition =
                (data.action === 'next' && index === 0) || // Modal opening
                (data.action === 'next' &&
                    ((onboardingStore.addTradesSubcase === 'manual' && index === 5) ||
                        (onboardingStore.addTradesSubcase === 'import' && index === 3))) || // Modal closing
                (data.action === 'prev' && index > 0); // Backward navigation

            if (needsTransition) {
                setIsTransitioning(true);
                const clearTimeout = safeTransitionTimeout();

                try {
                    // Handle modal opening transitions (step 0 -> step 1)
                    if (await handleModalOpeningTransition(data, helperParams)) {
                        return;
                    }

                    // Handle modal closing transitions (step 6 -> step 7 in manual)
                    if (await handleModalClosingTransition(data, helperParams)) {
                        return;
                    }

                    // Handle backward navigation
                    if (await handleBackwardNavigation(data, helperParams)) {
                        return;
                    }
                } finally {
                    setIsTransitioning(false);
                    clearTimeout();
                }
            } else {
                // Handle modal opening transitions (step 0 -> step 1)
                if (await handleModalOpeningTransition(data, helperParams)) {
                    return;
                }

                // Handle modal closing transitions (step 6 -> step 7 in manual)
                if (await handleModalClosingTransition(data, helperParams)) {
                    return;
                }

                // Handle backward navigation
                if (await handleBackwardNavigation(data, helperParams)) {
                    return;
                }
            }

            // Default forward navigation
            if (data.action === 'next' && data.lifecycle === 'complete') {
                onboardingStore.setStep(data.index + 1);
            }
        },
        [onboardingStore, validateTarget, validateTargetWithRetry, tradeStore, isTransitioning, safeTransitionTimeout],
    );

    return (
        <>
            <style>{pulseKeyframes}</style>
            {/* Custom overlay during transitions to prevent flickering */}
            {showOverlay && (
                <span className="pointer-events-none fixed top-0 left-0 z-[100000] h-full w-full bg-[rgba(0,0,0,0.75)]" />
            )}
            <Joyride
                key={`${onboardingStore.case}-${onboardingStore.addTradesSubcase}`} // Force re-render on subcase change
                steps={steps}
                run={onboardingStore.run && steps.length > 0 && !isTransitioning} // Only run if we have valid steps and not transitioning
                stepIndex={onboardingStore.stepIndex}
                continuous
                scrollToFirstStep={true} // Enable scrolling for page elements
                scrollOffset={0} // No scroll offset
                disableScrollParentFix={false} // Disable scroll parent fix
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        overlayColor: 'rgba(0,0,0,0.75)',
                        zIndex: 100000, // Higher than modals (1200) to ensure visibility
                        arrowColor: theme.palette.background.header,
                    },
                    spotlight: {
                        borderRadius: 80, // Perfect circle
                        animation: 'onboarding-pulse 1.5s ease-out infinite',
                    },
                    beacon: {
                        outline: 0, // Remove focus outline
                        backgroundColor: 'rgba(59, 130, 246, 0.8)',
                        borderRadius: 80,
                    },
                    tooltip: {
                        borderRadius: 12,
                        filter: 'drop-shadow(0 8px 30px rgba(0, 0, 0, 0.25))',
                        border: 'none',
                    },
                    tooltipContainer: {
                        textAlign: 'left',
                    },
                }}
                floaterProps={{
                    disableAnimation: true, // Enable animation for proper positioning
                }}
                tooltipComponent={(props) => (
                    <CustomTooltip {...props} isLast={onboardingStore.stepIndex === steps.length - 1} />
                )}
            />
        </>
    );
});
